import { faTimes, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { kml } from '@tmcw/togeojson';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
	Button,
	Form,
	FormControl,
	FormGroup,
	FormText
} from 'vio-components';

import { IPreviewFiber } from '../../interfaces';

interface IPreviewMenuProps {
	activePreviewFile: IPreviewFiber;
	/* adds the geojon converted file to the Map component */
	addPreviewFiber: (fiber: IPreviewFiber['geoJSON']) => void;
	/* adds the preview file upload to the redux store */
	addPreviewFile: (fiber: IPreviewFiber) => void;
}

export const PreviewMenu: React.FC<IPreviewMenuProps> = ({
	addPreviewFiber,
	addPreviewFile,
	activePreviewFile = null
}): JSX.Element => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [ previewFile, setPreviewFile ] = useState<IPreviewFiber>(activePreviewFile);
	const [ showUploadField, setShowUploadField ] = useState<boolean>(true);

	const cancelPreview = () => {
		addPreviewFile(null); // removes from redux
		addPreviewFiber(null); // removes from map
		setPreviewFile(null);
		setShowUploadField(false);
		setTimeout(() => setShowUploadField(true), 500); // forces upload field to reset value
	};

	useEffect(() => {
		setShowUploadField(!previewFile);

		if (previewFile) {
			const isKMLFile = previewFile?.name.indexOf('.kml') > -1;

			// from https://github.com/placemark/togeojson
			fetch(URL.createObjectURL(previewFile.file))
				.then((response) => {
					return isKMLFile ? response.text() : response.json();
				})
				.then((file) => {
					if (isKMLFile) {
						addPreviewFiber(kml(new DOMParser().parseFromString(file, 'text/xml')) as unknown as IPreviewFiber['geoJSON']);
					}
					else {
						addPreviewFiber(file as unknown as IPreviewFiber['geoJSON']);
					}

				}).catch((e) => {
					throw new Error(`error parsing preview ${e}`);
				});
		}
	}, [ previewFile ]);

	return (
		<div id='preview-panel' className='container' >
			<h4 className='mb-4'>Fiber Preview</h4>

			<Form>
				{ showUploadField && <FormGroup controlId='kml'>
					<FormControl
						className='file-upload'
						disabled={ !!previewFile }
						type='file'
						accept='.kml, .geojson'
						onChange={ (e: React.ChangeEvent<HTMLInputElement>) => {
							const { files } = e.target;
							const file = files[ 0 ];
							const { name } = file;

							addPreviewFile({ file, name });
							setPreviewFile({ file, name });
						} } />
				</FormGroup> }

				{ (!showUploadField && previewFile) && <>
					<FormGroup controlId='kml-display'>
						<FormControl
							readOnly
							disabled
							value={ previewFile.name } />
					</FormGroup>

					<div className='vio-form-footer mb-2'>
						<Button
							outline
							className='cancel'
							onClick={ cancelPreview }>
							{ t('CANCEL') } <FontAwesomeIcon icon={ faTimes } aria-hidden transform='down-1 right-2' />
						</Button>

						<Button
							className='submit'
							variant='success'
							onClick={ () => navigate('/assets/add?preview=true') }>
							Add to Map <FontAwesomeIcon icon={ faPlus } aria-hidden transform='down-1 right-2' />
						</Button>
					</div>

					<FormText>Preview is added, if it meets expectation continue to add it to the map permanently.</FormText>
				</> }
			</Form>
		</div>
	);
};

export default PreviewMenu;
